import React, { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Stack,
} from '@mui/material'
import { AreaFuncional, AreaFuncionalCreate } from '../../types/api'
import { gruposApi } from '../../api/grupos'
import { useQuery } from '@tanstack/react-query'

const createAreaFuncionalSchema = () =>
  z.object({
    grupo_id: z.number().min(1, 'Grupo é obrigatório'),
    nome: z.string().min(1, 'Nome é obrigatório').max(100, 'Nome deve ter no máximo 100 caracteres'),
    descricao: z.string().max(500, 'Descrição deve ter no máximo 500 caracteres').optional().nullable(),
    iduser: z.number().optional().nullable(),
  })

type AreaFuncionalFormData = z.infer<ReturnType<typeof createAreaFuncionalSchema>>

interface AreaFuncionalFormProps {
  open: boolean
  onClose: () => void
  onSubmit: (data: AreaFuncionalCreate) => Promise<void>
  area?: AreaFuncional | null
  loading?: boolean
}

const AreaFuncionalForm: React.FC<AreaFuncionalFormProps> = ({
  open,
  onClose,
  onSubmit,
  area,
  loading = false,
}) => {
  const { t } = useTranslation()
  const areaSchema = React.useMemo(() => createAreaFuncionalSchema(), [])

  const { data: grupos = [] } = useQuery({
    queryKey: ['grupos'],
    queryFn: () => gruposApi.list({ includeInactive: true }),
  })

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<AreaFuncionalFormData>({
    resolver: zodResolver(areaSchema),
    defaultValues: {
      grupo_id: area?.grupo_id || 0,
      nome: area?.nome || '',
      descricao: area?.descricao || '',
      iduser: area?.iduser || undefined,
    },
  })

  useEffect(() => {
    if (open) {
      reset({
        grupo_id: area?.grupo_id || 0,
        nome: area?.nome || '',
        descricao: area?.descricao || '',
        iduser: area?.iduser || undefined,
      })
    }
  }, [open, area, reset])

  const handleFormSubmit = async (data: AreaFuncionalFormData) => {
    await onSubmit(data)
    reset()
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <form onSubmit={handleSubmit(handleFormSubmit)}>
        <DialogTitle>
          {area ? t('common.edit', 'Editar') : t('common.create', 'Criar')} Área Funcional
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              select
              label="Grupo"
              fullWidth
              error={!!errors.grupo_id}
              helperText={errors.grupo_id?.message}
              {...register('grupo_id', {
                setValueAs: (v) => (v === '' || v === '0' ? 0 : parseInt(v, 10)),
              })}
              SelectProps={{
                native: true,
              }}
            >
              <option value={0}>Selecione um grupo</option>
              {[...grupos]
                .sort((a, b) => a.nome.localeCompare(b.nome, undefined, { sensitivity: 'base' }))
                .map((grupo) => (
                  <option key={grupo.id} value={grupo.id}>
                    {grupo.nome}
                  </option>
                ))}
            </TextField>

            <TextField
              label="Nome"
              fullWidth
              error={!!errors.nome}
              helperText={errors.nome?.message}
              {...register('nome')}
            />

            <TextField
              label="Descrição"
              fullWidth
              multiline
              rows={3}
              error={!!errors.descricao}
              helperText={errors.descricao?.message}
              {...register('descricao')}
            />

            <TextField
              label="ID User"
              type="number"
              fullWidth
              error={!!errors.iduser}
              helperText={errors.iduser?.message}
              {...register('iduser', { valueAsNumber: true })}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>{t('common.cancel', 'Cancelar')}</Button>
          <Button type="submit" variant="contained" disabled={loading}>
            {loading ? t('common.saving', 'Salvando...') : t('common.save', 'Salvar')}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  )
}

export default AreaFuncionalForm

